import React, { Component } from 'react';
import PropTypes from 'prop-types';
import AutocompleteListResult from './AutocompleteListResult';
import AutocompleteTableResult from './AutocompleteTableResult';
import Spinner from "./Spinner";

/*
 * Wrapper for autocomplete components. It includes a text input for the user to type
 * and it can take children, such as a dropdown of options. Selected items can be passed
 * and hidden inputs are generated for values to be correctly sent to the server.
 */
export default class AutocompleteInput extends Component {
  static propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    selected: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })),
    placeholder: PropTypes.string,
    multiple: PropTypes.bool,
    onKeyDown: PropTypes.func,
    onClick: PropTypes.func.isRequired,
    onClickSelected: PropTypes.func.isRequired,
    onClearAll: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    resultsAs: PropTypes.string.isRequired,
    resultsHeader: PropTypes.array,
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.node,
    ]),
    required: PropTypes.bool,
    triggerEvents: PropTypes.bool,
    innerRef: PropTypes.func,
    loadingSelected: PropTypes.bool
  }

  static defaultProps = {
    id: null,
    name: null,
    selected: [],
    placeholder: '',
    multiple: true,
    resultsAs: 'tags',
    resultsHeader: [],
    onClearAll() {},
    onKeyDown() {},
    children: null,
    required: false,
    triggerEvents: false,
    innerRef() {}
  }

  constructor(props) {
    super(props);

    this.state = {
      loadingSelected: props.loadingSelected,
      resultsFiltered: [],
      resultsQuery: ''
    };

    this.resultsTableInput = React.createRef();
  }

  triggerChangeEvent() {
    if (this.props.triggerEvents) {
      const $node = $(this.node).find("input[type='text']");
      $node.trigger('autocompleteinput:change');
    }
  }

  componentDidMount() {
    this.triggerChangeEvent();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selected.length !== this.props.selected.length) {
      this.triggerChangeEvent();
      this.filterResults(this.state.resultsQuery);
    }
  }

  hasQuery(string, query) {
    return string
      .split(' ')
      .filter((s) => {
        return s.toUpperCase().startsWith(query.toUpperCase())
      }).length > 0;
  }

  filterResults(query) {
    if (query !== '') {
      this.setState({
        resultsFiltered: this.props.selected
          .filter(s => {
            const matchesNames = s.name !== null ? this.hasQuery(s.name, query) : false;
            const matchesRoles = s.roles !== null ? this.hasQuery(s.roles, query) : false;
            const matchesCompany = s.company_name !== null ? this.hasQuery(s.company_name, query) : false;

            return matchesNames || matchesRoles || matchesCompany;
          }),
        resultsQuery: query
      });
    } else {
      this.setState({ resultsFiltered: this.props.selected, resultsQuery: '' });
    }
  }

  handleFilter = (e) => {
    const query = e.target.value;

    this.filterResults(query);
  }

  handleFilterClear = () => {
    this.setState({ resultsFiltered: this.props.selected, resultsQuery: '' });
    this.resultsTableInput.current.value = '';
  }

  renderSelected() {
    const selected = this.state.resultsQuery !== '' ? this.state.resultsFiltered : this.props.selected;
    switch (this.props.resultsAs) {
      case 'tags':
        return (
          <ul className="AutocompleteInput-selection">
            { this.props.selected.map((item) =>
              <AutocompleteListResult
                {...item}
                key={item.id}
                formName={this.props.name}
                onClick={this.props.onClickSelected.bind(this, item)} />
            )}
          </ul>
        );
      case 'table':
        if (this.props.selected.length > 0) {
          return (
            <div>
              <div className="u-flex u-justify-between u-items-center u-mt-0 u-mb-2">
                <div className="u-relative u-w-80">
                  <input
                    type="text"
                    style={{ marginBottom: 0 }}
                    ref={this.resultsTableInput}
                    placeholder={`Search ${this.props.selected.length} attendees`}
                    onChange={this.handleFilter}
                  />
                  <div className="u-absolute u-right-0 u-mr-2 u-inset-y-0 u-flex u-items-center">
                    { this.state.resultsQuery !== '' &&
                      <i className="Icon Icon-close u-text-xs u-cursor-pointer" onClick={this.handleFilterClear}></i>
                    }
                    { this.state.resultsQuery === '' &&
                      <i className="Icon Icon-search u-text-xs"></i>
                    }
                  </div>
                </div>
                <a className="Button Button--red" onClick={this.props.onClearAll}>
                  <i className="Icon Icon-delete u-mr-2"></i>
                  Remove all attendees
                </a>
              </div>
              <table className="u-w-full u-scrollable-table">
                <thead>
                  <tr>
                    { this.props.resultsHeader.map((item) =>
                      <th key={item}>{item}</th>
                    )}
                  </tr>
                </thead>
                <tbody className="u-max-h-sm">
                { selected.map((item) =>
                  <AutocompleteTableResult
                    id={item.id}
                    index={item.index}
                    name={item.name}
                    company_name={item.company_name}
                    avatar={item.avatar}
                    roles={item.roles}
                    key={item.id}
                    formName={this.props.name}
                    onDelete={this.props.onClickSelected.bind(this, item)}
                  />
                )}
                </tbody>
              </table>
            </div>

          );
        }
        return null;
      default:
        break;
    }
  }

  render() {
    const showInput = this.props.multiple || (!this.props.multiple && this.props.selected.length === 0);

    return (
      <div className="AutocompleteInput" ref={(node) => { this.node = node; }}>
        { showInput &&
          <input type="text"
            id={`${this.props.id}-input`}
            name={this.props.multiple ? null : this.props.name}
            placeholder={this.props.placeholder}
            onKeyDown={this.props.onKeyDown}
            onClick={this.props.onClick}
            onChange={this.props.onChange}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            className={this.props.required ? 'required' : 'optional'}
            ref={(node) => this.props.innerRef(node)}
          />
        }

        { this.props.loadingSelected && <Spinner />}
        { !this.props.loadingSelected && this.renderSelected() }

        { this.props.children }
      </div>
    );
  }
}
